import type { QueryResult, Visualization } from './api';

/** Widget kinds the dashboard auto-classifies a report result into. */
export type WidgetKind = 'kpi' | 'line' | 'bar' | 'donut' | 'table' | 'detail' | 'scatter';

export const WIDGET_KINDS: readonly WidgetKind[] = ['kpi', 'line', 'bar', 'donut', 'table', 'detail', 'scatter'];

export function isWidgetKind(v: unknown): v is WidgetKind {
  return typeof v === 'string' && (WIDGET_KINDS as readonly string[]).includes(v);
}

/** How the planner's `visualization` maps onto a dashboard widget. */
const VIZ_TO_WIDGET: Record<Visualization, WidgetKind> = {
  number: 'kpi',
  bar: 'bar',
  // A dashboard tile renders a horizontal bar as the same `bar` widget; the
  // orientation hint isn't carried into the tile grid (kept vertical there).
  bar_horizontal: 'bar',
  line: 'line',
  pie: 'donut',
  donut: 'donut',
  table: 'table',
  scatter: 'scatter',
};

/** Reverse of VIZ_TO_WIDGET: the visualization a widget kind renders as. */
export const WIDGET_TO_VIZ: Record<WidgetKind, Visualization> = {
  kpi: 'number',
  line: 'line',
  bar: 'bar',
  donut: 'donut',
  table: 'table',
  detail: 'table',
  scatter: 'scatter',
};

/** Human labels for the visualization picker. */
export const VIZ_LABELS: Record<Visualization, string> = {
  number: 'KPI',
  bar: 'Bar',
  bar_horizontal: 'Horizontal bar',
  line: 'Line',
  pie: 'Pie',
  donut: 'Donut',
  scatter: 'Scatter',
  table: 'Table',
};

const VIZ_ORDER: Visualization[] = ['number', 'bar', 'bar_horizontal', 'line', 'pie', 'donut', 'scatter', 'table'];
const VIZ_SET = new Set<Visualization>(VIZ_ORDER);

/** Whether a stored string is one of our visualization values. */
export function isVisualization(v: unknown): v is Visualization {
  return typeof v === 'string' && VIZ_SET.has(v as Visualization);
}

/**
 * The visualizations THIS result can render as (mirrors the Chart branches), in
 * display order. `table` is always available. Used to offer only the types that
 * make sense for the data in the Ask picker.
 */
export function supportedVisualizations(result: QueryResult): Visualization[] {
  const metrics = result.columns.filter((c) => c.kind === 'metric').length;
  const dims = result.columns.filter((c) => c.kind === 'dimension').length;
  const hasTime = result.columns.some((c) => c.kind === 'time');
  const labelAxes = dims + (hasTime ? 1 : 0);
  const rows = result.rows.length;
  const ok = new Set<Visualization>(['table']);
  if (metrics >= 1 && labelAxes === 0 && rows <= 1) ok.add('number'); // single aggregate value
  if (metrics === 1 && labelAxes === 1) {
    ok.add('bar'); ok.add('bar_horizontal'); ok.add('line');
    if (!hasTime) { ok.add('pie'); ok.add('donut'); } // categorical share only
  }
  if (metrics === 1 && labelAxes === 2) { ok.add('line'); ok.add('bar'); ok.add('bar_horizontal'); } // multi-series
  if (metrics >= 2 && labelAxes >= 1) ok.add('scatter'); // two measures against each other
  return VIZ_ORDER.filter((v) => ok.has(v));
}

/**
 * The viz + widget kind a dashboard tile should use: the report's SAVED
 * visualization when it's valid and still renderable, else auto-classified by shape.
 */
export function resolveTileViz(report: { widget?: string }, result: QueryResult): { viz: Visualization; kind: WidgetKind } {
  const saved = report.widget;
  if (isVisualization(saved) && supportedVisualizations(result).includes(saved)) {
    return { viz: saved, kind: VIZ_TO_WIDGET[saved] };
  }
  const kind = widgetFor(result);
  return { viz: WIDGET_TO_VIZ[kind], kind };
}

/**
 * The widget to render a report as.
 *
 * The planner's `visualization` wins when it set one — it encodes what the
 * question actually asked for ("…as a bar chart"), which the shape heuristic
 * below cannot know. Only when the plan is silent, or asks for something this
 * result cannot render, do we fall back to classifying by shape.
 *
 * This ordering matters: `classifyWidget` alone turns any 1-dimension,
 * 1-metric result with ≤8 rows into a donut, so an explicit "as a bar chart"
 * came out as a pie on the template dashboard even though the API answered
 * `visualization: "bar"`.
 */
export function widgetFor(result: QueryResult): WidgetKind {
  const planned = result.plan?.visualization;
  const kind = planned ? VIZ_TO_WIDGET[planned] : undefined;
  if (kind && canRender(kind, result)) {
    // A planned table still splits into small vs. long, as the heuristic does.
    return kind === 'table' && result.rows.length > DETAIL_ROWS ? 'detail' : kind;
  }
  return classifyWidget(result);
}

/**
 * Whether this result can actually be drawn as `kind`. Guards against honoring a
 * plan that would render empty or lose data — e.g. a bar chart over two grouping
 * dimensions, or a single-value KPI over many rows.
 */
function canRender(kind: WidgetKind, result: QueryResult): boolean {
  const metrics = result.columns.filter((c) => c.kind === 'metric').length;
  const dims = result.columns.filter((c) => c.kind === 'dimension').length;
  const hasTime = result.columns.some((c) => c.kind === 'time');

  if (kind === 'table' || kind === 'detail') return true;
  if (kind === 'kpi') return metrics >= 1 && result.rows.length <= 1;
  if (kind === 'scatter') return metrics >= 2 && dims >= 1; // two measures (x vs y) + a label
  if (metrics !== 1) return false; // every chart below draws exactly one measure
  // A time axis plus one category is the multi-series line Chart already supports.
  if (kind === 'line') return hasTime ? dims <= 1 : dims === 1;
  return dims + (hasTime ? 1 : 0) === 1; // bar / donut: exactly one label axis
}

/** Above this many rows a table is laid out full-width as a detail table. */
const DETAIL_ROWS = 15;

/**
 * Auto-classify a live result into a dashboard widget (§ Report Templates plan):
 * single value → KPI; time series → line; one category → donut (≤8) / bar; many
 * dimensions or measures → table; large raw result → long detail table.
 *
 * Shape-only — it never sees the question. Prefer {@link widgetFor}, which
 * consults the plan first and falls back to this.
 */
export function classifyWidget(result: QueryResult): WidgetKind {
  const metrics = result.columns.filter((c) => c.kind === 'metric');
  const dims = result.columns.filter((c) => c.kind === 'dimension');
  const hasTime = result.columns.some((c) => c.kind === 'time');
  const rows = result.rows.length;

  if (metrics.length >= 1 && dims.length === 0 && !hasTime && rows <= 1) return 'kpi';
  if (hasTime && metrics.length >= 1 && dims.length <= 1) return 'line';
  if (dims.length >= 2 || metrics.length >= 2) return rows > DETAIL_ROWS ? 'detail' : 'table';
  if (dims.length === 1 && metrics.length === 1) return rows <= 8 ? 'donut' : 'bar';
  return rows > DETAIL_ROWS ? 'detail' : 'table';
}

/** Which of the two dashboard column-grids a widget belongs to (charts vs. tables). */
export function widgetGroup(kind: WidgetKind): 'kpi' | 'chart' | 'table' | 'detail' {
  if (kind === 'kpi') return 'kpi';
  if (kind === 'line' || kind === 'bar' || kind === 'donut' || kind === 'scatter') return 'chart';
  if (kind === 'detail') return 'detail';
  return 'table';
}

// ── Dashboard tile sizing (12-column grid) ───────────────────────────────────

/** The only widths a tile snaps to: ¼, ⅓, ½, full of a 12-column row. */
export const GRID_SPANS = [3, 4, 6, 12] as const;
export type GridSpan = (typeof GRID_SPANS)[number];

export function isGridSpan(v: unknown): v is GridSpan {
  return typeof v === 'number' && (GRID_SPANS as readonly number[]).includes(v);
}

/**
 * The default width for a tile when the tenant hasn't saved one — derived from
 * the widget kind and how much data it carries, so a dashboard reads well before
 * anyone arranges it: a KPI is a quarter, a compact chart a third, a busy chart a
 * half, a big/detailed table full width.
 */
export function autoSpan(kind: WidgetKind, categoryCount: number, rowCount: number): GridSpan {
  switch (kind) {
    case 'kpi': return 3;
    case 'donut': return 4;
    case 'bar': return categoryCount > 6 ? 6 : 4;
    case 'line': return 6;
    case 'scatter': return 6;
    case 'detail': return 12;
    case 'table': return rowCount > 8 ? 12 : 6;
    default: return 6;
  }
}

/** Snap an arbitrary (fractional) column count to the nearest allowed span. */
export function snapSpan(cols: number): GridSpan {
  return GRID_SPANS.reduce((best, s) => (Math.abs(s - cols) < Math.abs(best - cols) ? s : best), GRID_SPANS[0]);
}
